import { getDomain } from "tldts";

const STRIPPABLE_SUBDOMAIN_LABELS = new Set(["api", "www", "v1", "v2"]);
const UNKNOWN_SLUG = "unknown-provider";
const UNKNOWN_DISPLAY = "Unknown provider";

function parseHost(baseUrl: string): string | null {
  if (!baseUrl) return null;
  try {
    const { hostname } = new URL(baseUrl);
    if (!hostname) return null;
    if (hostname === "localhost") return null;
    if (/^\d+\.\d+\.\d+\.\d+$/.test(hostname)) return null;
    if (hostname.includes(":")) return null;
    return hostname.toLowerCase();
  } catch {
    return null;
  }
}

/**
 * Extract the registrable apex (e.g. `weather.com`, `bbc.co.uk`) from a baseUrl.
 *
 * Uses the public suffix list via `tldts` so multi-part TLDs (`.co.uk`, `.com.au`)
 * collapse correctly. Falls back to a manual "strip strippable subdomain + keep last
 * 2 labels" approach for hosts the public suffix list does not recognize.
 */
function apexHost(baseUrl: string): string | null {
  const host = parseHost(baseUrl);
  if (!host) return null;

  // Primary path: public-suffix-aware extraction.
  const domain = getDomain(host);
  if (domain) return domain;

  // Fallback for unknown TLDs / private domains: manual strip.
  let labels = host.split(".");
  while (labels.length > 2 && STRIPPABLE_SUBDOMAIN_LABELS.has(labels[0])) {
    labels = labels.slice(1);
  }
  if (labels.length > 2) labels = labels.slice(-2);
  if (labels.length < 2) return null;
  return labels.join(".");
}

export function apexSlug(baseUrl: string): string {
  const apex = apexHost(baseUrl);
  if (!apex) return UNKNOWN_SLUG;
  return apex.replace(/\./g, "-");
}

export function providerDisplayName(baseUrl: string): string {
  const apex = apexHost(baseUrl);
  return apex ?? UNKNOWN_DISPLAY;
}

export function providerFaviconUrl(apex: string): string {
  return `https://www.google.com/s2/favicons?domain=${apex}&sz=64`;
}
